import { create } from "zustand";
import { applyTheme, resolvePalette } from "~/lib/theme";

/**
 * "real"  : 実データを使用（デフォルト）
 * "set"   : 写真設定済み状態を強制（コーチマーク非表示）
 * "unset" : 写真未設定状態を強制（コーチマーク表示）
 */
export type AvatarCoachmarkDebug = "real" | "set" | "unset";

const CYCLE: AvatarCoachmarkDebug[] = ["real", "set", "unset"];

/**
 * "real"     : 実データ（BEスケジュール）を使用（デフォルト）
 * "counting" : カウントダウン中状態を強制（実データを待たず見た目確認用）
 * "live"     : 「配信中」CTA状態を強制
 */
export type EimiCountdownDebug = "real" | "counting" | "live";

const EIMI_COUNTDOWN_CYCLE: EimiCountdownDebug[] = ["real", "counting", "live"];

/**
 * "real" : 実データ（get_user_inf の preferred_personalities）を使用（デフォルト）
 * "show" : 「あなたの好みに合う子」表示状態を強制
 * "hide" : 「あなたの好みに合う子」非表示状態を強制
 */
export type PreferenceMatchDebug = "real" | "show" | "hide";

const PREFERENCE_MATCH_CYCLE: PreferenceMatchDebug[] = ["real", "show", "hide"];

/**
 * "real"      : BE の割り付け（enroll API）を使用（デフォルト）
 * "control"   : 現行スキップUI（吹き出し右上）を強制
 * "treatment" : 控えめスキップUI（吹き出し下部）を強制
 */
export type HighReviewSkipVariantDebug = "real" | "control" | "treatment";

const HIGH_REVIEW_SKIP_VARIANT_CYCLE: HighReviewSkipVariantDebug[] = [
  "real",
  "control",
  "treatment",
];

/**
 * "real"      : 実データ（get_comeback_purchase_plan）を使用（デフォルト）
 * "low"       : 対象・低価格帯のコース表示を強制（層1相当）
 * "tier2"     : 対象・層2相当のコース表示を強制（候補2件のうち高い方を固定で表示）
 * "tier3"     : 対象・層3相当のコース表示を強制（候補2件のうち高い方を固定で表示）
 * "highValue" : 対象・高額帯のコース表示を強制（層4相当）
 *
 * サーバー側は層2・層3（候補2件の層）でもA/Bテストでどちらか1件だけを返すため、
 * QA用の疑似データも常に1コース表示になる（2コース同時表示のパターンは無い）。
 * tier2/tier3はA/Bのランダム抽選結果の一例を固定で見せているだけで、ランダム性は無い
 */
export type ComebackPromotionDebug =
  | "real"
  | "low"
  | "tier2"
  | "tier3"
  | "highValue";

const COMEBACK_PROMOTION_CYCLE: ComebackPromotionDebug[] = [
  "real",
  "low",
  "tier2",
  "tier3",
  "highValue",
];

/**
 * "real"      : BE の割り付け（enroll API）を使用（デフォルト）
 * "control"   : 既存の5問を強制
 * "treatment" : 進め方を聞く8問を強制
 */
export type OnboardingQuestionsVariantDebug = "real" | "control" | "treatment";

const ONBOARDING_QUESTIONS_VARIANT_CYCLE: OnboardingQuestionsVariantDebug[] = [
  "real",
  "control",
  "treatment",
];

/**
 * "real"      : 固定しない（8問の群なら出す。coco+ は出さない）（デフォルト）
 * "control"   : 顔の系統を出さない
 * "treatment" : 顔の系統を出す
 */
export type FaceTypeVariantDebug = "real" | "control" | "treatment";

const FACE_TYPE_VARIANT_CYCLE: FaceTypeVariantDebug[] = [
  "real",
  "control",
  "treatment",
];

/**
 * "real"    : 実データ（is_target・開催状況・表示期間）を使用（デフォルト）
 * "count"   : 集計中のバナーを強制
 * "receive" : 受け取り中のバナーを強制
 */
export type NewUserPointBackBannerDebug = "real" | "count" | "receive";

const NEW_USER_POINT_BACK_BANNER_CYCLE: NewUserPointBackBannerDebug[] = [
  "real",
  "count",
  "receive",
];

type DebugState = {
  avatarCoachmarkDebug: AvatarCoachmarkDebug;
  cycleAvatarCoachmarkDebug: () => void;
  /**
   * true の間、新規登録直後の遷移先を /onboarding ではなく /discover に固定する
   * （好みタイプ選択オンボーディング開発中、他機能の動作確認をしやすくするための一時的なローカル切り替え）
   */
  skipOnboardingRedirect: boolean;
  toggleSkipOnboardingRedirect: () => void;
  /** デバッグ用テーマ上書き（null = 実アプリのテーマをそのまま使用） */
  debugThemeAppId: string | null;
  setDebugThemeAppId: (appId: string | null) => void;
  /** true の間、プロフィール取得APIをcode:60(ブロック)エラーとして再現する */
  forceBlockedProfileError: boolean;
  toggleForceBlockedProfileError: () => void;
  /** true の間、プロフィール取得APIを未対応コードのエラー(汎用エラーモーダル表示)として再現する */
  forceUnknownProfileError: boolean;
  toggleForceUnknownProfileError: () => void;
  /** ライブ一覧のえいみキャンペーンカウントダウンバナー状態強制（キャンペーン期間外でも見た目確認できるように） */
  eimiCountdownDebug: EimiCountdownDebug;
  cycleEimiCountdownDebug: () => void;
  /** さがす画面「あなたの好みに合う子」表示ゲート状態強制（好み未保存アカウントでも見た目確認できるように） */
  preferenceMatchDebug: PreferenceMatchDebug;
  cyclePreferenceMatchDebug: () => void;
  /** true の間、さがす画面の秋のポイントバック訴求カードを開催状態に関わらず表示する（開催前でも見た目・導線を確認できるように） */
  forceAutumnPointBackPromo: boolean;
  toggleForceAutumnPointBackPromo: () => void;
  /** 新規会員向けポイントバックのバナーの段階を強制する（対象外アカウント・期間外でも見た目と遷移を確認できるように） */
  newUserPointBackBannerDebug: NewUserPointBackBannerDebug;
  cycleNewUserPointBackBannerDebug: () => void;
  /**
   * true の間、課金経験の判定を「未課金」に固定する。
   * チャット pt不足の先回りモーダルは未課金者限定なので、一度課金した
   * QA アカウントでは再検証できなくなる。それを回避するための上書き。
   */
  forceNeverPurchased: boolean;
  setForceNeverPurchased: (value: boolean) => void;
  /**
   * true の間、初回購入限定コース（can_buy_first_bonus_course）が使える扱いにする。
   * 通話 pt不足モーダルの CTA のあとに挟むバナーは初回コースが使える人にしか出ないので、
   * 一度課金した QA アカウントでも見られるようにする上書き。
   */
  forceFirstBonusCourse: boolean;
  setForceFirstBonusCourse: (value: boolean) => void;
  /**
   * 高評価ユーザー紹介オンボのスキップUI A/B の群を強制する。
   * 群は user_id で決まるため、QA アカウントでは片方しか確認できない。
   * "real" 以外の間は enroll API を呼ばない（到達ログを汚さないため）。
   */
  highReviewSkipVariantDebug: HighReviewSkipVariantDebug;
  cycleHighReviewSkipVariantDebug: () => void;
  onboardingQuestionsVariantDebug: OnboardingQuestionsVariantDebug;
  cycleOnboardingQuestionsVariantDebug: () => void;
  faceTypeVariantDebug: FaceTypeVariantDebug;
  cycleFaceTypeVariantDebug: () => void;
  /**
   * おかえりプラン（休眠復帰限定ポイント購入プロモーション）の表示を強制する。
   * 対象条件（7日ログインなし等）を満たさないQAアカウントでも、さがす画面の
   * ポップアップ・購入画面上部バナーの見た目を確認できるようにする上書き。
   */
  comebackPromotionDebug: ComebackPromotionDebug;
  cycleComebackPromotionDebug: () => void;
};

export const useDebugStore = create<DebugState>()((set) => ({
  avatarCoachmarkDebug: "real",
  cycleAvatarCoachmarkDebug: () =>
    set((s) => ({
      avatarCoachmarkDebug:
        CYCLE[(CYCLE.indexOf(s.avatarCoachmarkDebug) + 1) % CYCLE.length],
    })),
  debugThemeAppId: null,
  setDebugThemeAppId: (appId) => {
    applyTheme(resolvePalette(appId));
    set({ debugThemeAppId: appId });
  },
  skipOnboardingRedirect: false,
  toggleSkipOnboardingRedirect: () =>
    set((s) => ({ skipOnboardingRedirect: !s.skipOnboardingRedirect })),
  forceBlockedProfileError: false,
  toggleForceBlockedProfileError: () =>
    set((s) => ({ forceBlockedProfileError: !s.forceBlockedProfileError })),
  forceUnknownProfileError: false,
  toggleForceUnknownProfileError: () =>
    set((s) => ({ forceUnknownProfileError: !s.forceUnknownProfileError })),
  eimiCountdownDebug: "real",
  cycleEimiCountdownDebug: () =>
    set((s) => ({
      eimiCountdownDebug:
        EIMI_COUNTDOWN_CYCLE[
          (EIMI_COUNTDOWN_CYCLE.indexOf(s.eimiCountdownDebug) + 1) %
            EIMI_COUNTDOWN_CYCLE.length
        ],
    })),
  preferenceMatchDebug: "real",
  cyclePreferenceMatchDebug: () =>
    set((s) => ({
      preferenceMatchDebug:
        PREFERENCE_MATCH_CYCLE[
          (PREFERENCE_MATCH_CYCLE.indexOf(s.preferenceMatchDebug) + 1) %
            PREFERENCE_MATCH_CYCLE.length
        ],
    })),
  forceAutumnPointBackPromo: false,
  toggleForceAutumnPointBackPromo: () =>
    set((s) => ({ forceAutumnPointBackPromo: !s.forceAutumnPointBackPromo })),
  newUserPointBackBannerDebug: "real",
  cycleNewUserPointBackBannerDebug: () =>
    set((s) => ({
      newUserPointBackBannerDebug:
        NEW_USER_POINT_BACK_BANNER_CYCLE[
          (NEW_USER_POINT_BACK_BANNER_CYCLE.indexOf(
            s.newUserPointBackBannerDebug,
          ) +
            1) %
            NEW_USER_POINT_BACK_BANNER_CYCLE.length
        ],
    })),
  forceNeverPurchased: false,
  setForceNeverPurchased: (value) => set({ forceNeverPurchased: value }),
  forceFirstBonusCourse: false,
  setForceFirstBonusCourse: (value) => set({ forceFirstBonusCourse: value }),
  highReviewSkipVariantDebug: "real",
  cycleHighReviewSkipVariantDebug: () =>
    set((s) => ({
      highReviewSkipVariantDebug:
        HIGH_REVIEW_SKIP_VARIANT_CYCLE[
          (HIGH_REVIEW_SKIP_VARIANT_CYCLE.indexOf(
            s.highReviewSkipVariantDebug,
          ) +
            1) %
            HIGH_REVIEW_SKIP_VARIANT_CYCLE.length
        ],
    })),
  onboardingQuestionsVariantDebug: "real",
  cycleOnboardingQuestionsVariantDebug: () =>
    set((s) => ({
      onboardingQuestionsVariantDebug:
        ONBOARDING_QUESTIONS_VARIANT_CYCLE[
          (ONBOARDING_QUESTIONS_VARIANT_CYCLE.indexOf(
            s.onboardingQuestionsVariantDebug,
          ) +
            1) %
            ONBOARDING_QUESTIONS_VARIANT_CYCLE.length
        ],
    })),
  faceTypeVariantDebug: "real",
  cycleFaceTypeVariantDebug: () =>
    set((s) => ({
      faceTypeVariantDebug:
        FACE_TYPE_VARIANT_CYCLE[
          (FACE_TYPE_VARIANT_CYCLE.indexOf(s.faceTypeVariantDebug) + 1) %
            FACE_TYPE_VARIANT_CYCLE.length
        ],
    })),
  comebackPromotionDebug: "real",
  cycleComebackPromotionDebug: () =>
    set((s) => ({
      comebackPromotionDebug:
        COMEBACK_PROMOTION_CYCLE[
          (COMEBACK_PROMOTION_CYCLE.indexOf(s.comebackPromotionDebug) + 1) %
            COMEBACK_PROMOTION_CYCLE.length
        ],
    })),
}));
